"use client";

import { cn } from "@/lib/utils";
import ImgHiw1 from "@/assets/images/img_hiw_1.webp"
import ImgHiw2 from "@/assets/images/img_hiw_2.webp"
import ImgHiw3 from "@/assets/images/img_hiw_3.webp"
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

interface StepCardProps {
  stepNumber: number;
  title: string;
  description: string;
  imageSrc?: string;
}

function StepCard({ stepNumber, title, description, imageSrc }: StepCardProps) {
  const descRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    const ctx = gsap.context( async () => {
      if(descRef.current){
        const descChildren = Array.from(descRef.current.children);
        descChildren.forEach((child) => {
          gsap.set(
            child, 
            { opacity: 0, mixBlendMode: "multiply" }
          );
        });
        gsap.fromTo(
          descChildren,
          { opacity: 0, },
          { 
            opacity: 1, 
            scrollTrigger: {
              trigger: descRef.current,
              start: "top bottom",
              end: "bottom bottom",
              scrub: 1,
            },
          },
        );
      }
    });

    return () => ctx.revert();
  });

  return (
    <div className="flex flex-col">
      <img 
        src={imageSrc} 
        alt={title} 
        className={cn(
          "mix-blend-multiply object-contain px-4",
          "md:translate-x-[-48px] md:translate-y-4"
        )}
      />
      <div ref={descRef} className={cn(
        "flex flex-col text-center",
        "md:text-left md:pb-10 2k:pb-20 4k:pb-30"
      )}>
        <span 
          className={cn(
            "flex items-center justify-center gap-2 text-[22px] mb-6",
            "md:justify-start",
            "2k:text-[36px] 2k:gap-6 2k:mb-8",
            "4k:text-[56px] 4k:gap-6 4k:mb-12"
          )}
        >
          <p>[{stepNumber}]</p>
          <p className="font-bold">{title}</p>
        </span>
        <p className="text-[20px] 2k:text-[30px] 4k:text-[50px]">{description}</p>
      </div>
    </div>
  );
}

interface HowItWorksSectionProps extends React.HTMLAttributes<HTMLElement> {}

function HowItWorksSection({ className, ...props }: HowItWorksSectionProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const stepsData = t("howItWorks.steps", { returnObjects: true }) as { title: string; description: string }[];
  const imageSrcs = [ImgHiw1, ImgHiw2, ImgHiw3];

  const steps: StepCardProps[] = stepsData.map((step, index) => ({
    stepNumber: index + 1,
    title: step.title,
    description: step.description,
    imageSrc: imageSrcs[index],
  }));

  useGSAP(() => {
    const ctx = gsap.context( async () => {
      if(titleRef.current){
        gsap.set(
          titleRef.current, 
          { opacity: 0 }
        );
        gsap.fromTo(
          titleRef.current,
          { opacity: 0 },
          { 
            opacity: 1, 
            scrollTrigger: {
              trigger: titleRef.current,
              start: "top 85%",
              end: "top 40%",
              scrub: 1,
            },
          },
        );
      }

      if(stepsRef.current){
        gsap.set(
          stepsRef.current, 
          { opacity: 0, y: 60 }
        );
        gsap.fromTo(
          stepsRef.current,
          { opacity: 0, y: 60 },
          { 
            opacity: 1, 
            y: 0,
            scrollTrigger: {
              trigger: titleRef.current,
              start: "top center",
              end: "center center",
              scrub: 1,
            },
          },
        );
        const stepChildren = Array.from(stepsRef.current.children);
        stepChildren.forEach((child) => {
          gsap.set(
            child, 
            { opacity: 0, y: 20, mixBlendMode: "multiply" }
          );
        });
        gsap.fromTo(
          stepChildren,
          { opacity: 0, y: 20 },
          { 
            opacity: 1, 
            y: 0,
            stagger: 0.1,
            scrollTrigger: {
              trigger: stepsRef.current,
              start: "top center",
              end: "40% center",
              scrub: 1,
            },
          },
        );
      }
    });

    return () => ctx.revert();
  });

  return (
    <section
      id="how-it-works"
      className={cn("px-6", className)}
      {...props}
    >
      <h2 ref={titleRef} className="font-h1 mb-4 2k:mb-8 4k:mb-12">{t("howItWorks.title")}</h2>

      <div 
        ref={stepsRef} 
        className={cn(
          "grid grid-cols-1 md:grid-cols-3 px-8 bg-lavender-dark rounded-lg pb-10",
          "md:gap-12 md:pb-0",
          "2k:rounded-2xl 2k:gap-16 2k:px-16",
          "4k:rounded-4xl 4k:gap-24 4k:px-24"
        )}>
        {steps.map((step) => (
          <StepCard key={step.stepNumber} {...step} />
        ))}
      </div>
    </section>
  );
}

export { HowItWorksSection, StepCard };
